/*
 *   Copyright (c) 2025 Luxembourg Institute of Science and Technology
 *   All rights reserved.
 */

import { Col, Divider, Form, Input, List, Row, Select, Skeleton } from "antd"
import { graphql, useStaticQuery } from "gatsby"
import { Search } from "js-search"
import { filter, isEmpty, map } from "lodash"
import * as React from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import InventoryResourceCard from "./InventoryResourceCard"
import LabelWithTooltip from "./LabelWithTooltip"

const SearchComponent = ({
  elementOnClick,
}: {
  elementOnClick: () => void
}) => {
  const data = useStaticQuery(graphql`
    query {
      allSitePage {
        nodes {
          path
          pageContext
        }
      }
    }
  `)

  // Helper to parse value as array or string
  function parseArrayField(val: unknown): string[] {
    if (Array.isArray(val)) return val
    if (typeof val === "string") {
      try {
        // Try to parse as JSON array
        const arr = JSON.parse(val)
        if (Array.isArray(arr)) return arr
      } catch {}
      // Fallback: treat as comma-separated string
      return val
        .split(",")
        .map(v => v.trim())
        .filter(Boolean)
    }
    return []
  }

  // Define the extended type for indexed pages with parsed arrays
  type IndexedPage = {
    id: string
    path: string
    slug: string
    title: string
    description: string
    resourceType: string
    topics: string[]
    stakeholders: string[]
    topicsText: string
    stakeholdersText: string
  }

  // Prepare the documents to index (with pre-parsed array fields)
  const pages: IndexedPage[] = React.useMemo(
    () =>
      map(
        filter(
          data.allSitePage.nodes,
          page => page.pageContext && page.pageContext.title,
        ),
        page => {
          const topics = parseArrayField(page.pageContext.topics || [])
          const stakeholders = parseArrayField(
            page.pageContext.stakeholders || [],
          )

          return {
            id: page.path,
            path: page.path,
            slug: page.path
              .replace(/^\/inventory-resource\//, "")
              .replace(/\/$/, ""),
            title: page.pageContext.title,
            description: page.pageContext.description || "",
            resourceType: page.pageContext.resourceType || "",
            topics: topics,
            stakeholders: stakeholders,
            // Create searchable text fields for array data
            topicsText: topics.join(" "),
            stakeholdersText: stakeholders.join(" "),
          }
        },
      ),
    [data.allSitePage.nodes],
  )

  // Build the search index once with all metadata fields
  const search = React.useMemo(() => {
    const search = new Search("path")
    search.addIndex("title")
    search.addIndex("description")
    search.addIndex("resourceType")
    search.addIndex("topicsText")
    search.addIndex("stakeholdersText")
    search.addDocuments(pages)
    return search
  }, [pages])

  const [query, setQuery] = React.useState("")
  const [selectedResourceTypes, setSelectedResourceTypes] = React.useState<
    string[]
  >([])
  const [selectedTopics, setSelectedTopics] = React.useState<string[]>([])
  const [selectedStakeholders, setSelectedStakeholders] = React.useState<
    string[]
  >([])

  // Extract unique filter options from pages (using pre-parsed arrays)
  const resourceTypeOptions = React.useMemo(() => {
    const set = new Set<string>()
    pages.forEach(p => p.resourceType && set.add(p.resourceType.trim()))
    return Array.from(set).filter(Boolean)
  }, [pages])
  const topicsOptions = React.useMemo(() => {
    const set = new Set<string>()
    pages.forEach(p => p.topics.forEach((t: string) => set.add(t)))
    return Array.from(set).filter(Boolean)
  }, [pages])
  const stakeholdersOptions = React.useMemo(() => {
    const set = new Set<string>()
    pages.forEach(p => p.stakeholders.forEach((s: string) => set.add(s)))
    return Array.from(set).filter(Boolean)
  }, [pages])

  // Filter results by selected filters (optimized with pre-parsed arrays)
  const filteredResults = React.useMemo<IndexedPage[]>(() => {
    return (
      !isEmpty(query) ? (search.search(query) as IndexedPage[]) : pages
    ).filter(item => {
      const matchResourceType =
        selectedResourceTypes.length === 0 ||
        selectedResourceTypes.includes(item.resourceType.trim())
      const matchTopics =
        selectedTopics.length === 0 ||
        selectedTopics.some(topic => item.topics.includes(topic))
      const matchStakeholders =
        selectedStakeholders.length === 0 ||
        selectedStakeholders.some(stakeholder =>
          item.stakeholders.includes(stakeholder),
        )
      return matchResourceType && matchTopics && matchStakeholders
    })
  }, [
    query,
    search,
    pages,
    selectedResourceTypes,
    selectedTopics,
    selectedStakeholders,
  ])

  return (
    <Form name="searchForm" layout="vertical">
      <Row>
        <Col xs={24} md={12}>
          <Form.Item
            label={
              <LabelWithTooltip
                label="Search"
                tooltip="Search text in title and description"
              />
            }
            name="searchQuery"
          >
            <Input
              type="search"
              placeholder="Enter keywords to search inventory resources"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Resource Type">
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by resource type"
              value={selectedResourceTypes}
              onChange={setSelectedResourceTypes}
              options={resourceTypeOptions.map(rt => ({
                value: rt,
                label: rt,
              }))}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24} md={12}>
          <Form.Item label="Topics">
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by topics"
              value={selectedTopics}
              onChange={setSelectedTopics}
              options={topicsOptions.map(t => ({ value: t, label: t }))}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Stakeholders">
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by stakeholders"
              value={selectedStakeholders}
              onChange={setSelectedStakeholders}
              options={stakeholdersOptions.map(s => ({ value: s, label: s }))}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider
        style={{
          display: filteredResults.length > 0 ? "block" : "none",
        }}
      />
      <div
        id="scrollableDiv"
        style={{
          height: 400,
          overflow: "auto",
          padding: "0 16px",
          border: "1px solid rgba(140, 140, 140, 0.35)",
        }}
      >
        <InfiniteScroll
          dataLength={filteredResults.length}
          next={() => {}}
          hasMore={false}
          loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
          endMessage={
            <Divider
              style={{ display: filteredResults.length > 0 ? "block" : "none" }}
              plain
            >
              It is all, nothing more 🤐
            </Divider>
          }
          scrollableTarget="scrollableDiv"
        >
          <List
            dataSource={filteredResults}
            renderItem={item => (
              <List.Item key={item.path}>
                <div onClick={elementOnClick} style={{ width: "100%" }}>
                  <InventoryResourceCard resource={item} />
                </div>
              </List.Item>
            )}
          />
        </InfiniteScroll>
      </div>
    </Form>
  )
}

export default SearchComponent
